import { useEffect, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";

import { useAccessControl } from "../../../../app/access-control-context";
import { INQUIRY_EDIT } from "../../../../data/permissions";
import { useAppointmentFormModal } from "../../../../forms/appointment/appointment-form";
import type { Inquiry, InquiryAppointment } from "../../../../hooks/use-inquiries";
import {
  useCreateOrUpdateInquiry,
  useInquiry,
  useInquiryDashboard,
  useInquiryAppointments,
  useInquirySummary
} from "../../../../hooks/use-inquiries";
import { useCenterConstants } from "../../../../hooks/use-center";
import { LoadingOverlay } from "../../../loading-overlay/loading-overlay";
import { useFamilyContactDetailFormModal } from "../edit-family-contact-form";
import { useStudentDetailFormModal } from "../edit-student-contact-form";
import { StudentDetailsTable } from "./student-detail-table";
import { UpcomingEventsTable } from "./upcoming-events-table";
import { FamilyContactsTable } from "./family-contacts-table";
import { AppointmentHistoryAccordion } from "./appointment-history-accordion";
import { useAppointmentSummaryModal } from "./appointment-summary";
import { LoadingButton } from "../../../loading-button/loading-button";
import { ServiceIdSummary } from "./service-id-summary";
import type { UpdateAppointmentStatusOptions } from "./status-dropdown";
import { withUpdatedSelectedSubService } from "./learning-center-subservice";
import { cleanupOrphanedAppointmentModalArtifacts } from "./appointment-modal-cleanup";

export const InquiryDashboardInfo: FC<{ inquiry: Inquiry; onSelectServiceId?: (inquiryId: string) => void }> = ({
  inquiry,
  onSelectServiceId
}) => {
  const navigate = useNavigate();
  const [activeAppointment, setActiveAppointment] = useState<InquiryAppointment>();
  const [selectedUpcomingAppointment, setSelectedUpcomingAppointment] = useState<InquiryAppointment>();
  const { activeCenterId, hasPermission } = useAccessControl();
  const { activeConstants } = useCenterConstants(activeCenterId);
  const dashboardQuery = useInquiryDashboard({
    centerId: activeCenterId,
    inquiryId: inquiry.id
  });
  const inquiryDetailQuery = useInquiry(inquiry.id);
  const inquirySummaryQuery = useInquirySummary(inquiry.id);
  const createOrUpdateInquiryMutator = useCreateOrUpdateInquiry();
  const { createEventMutator, updateEventMutator } = useInquiryAppointments({
    centerId: activeCenterId,
    inquiryId: inquiry.id
  });

  const { showFamilyContactFormModal, renderFamilyContactFormModal } = useFamilyContactDetailFormModal();

  const handleSelectContact = (contactId: number) => {
    showFamilyContactFormModal({ contactId, inquiryId: inquiry.id });
  };

  const applyAppointmentStatusUpdate = (appointment: InquiryAppointment) => {
    updateEventMutator.mutate(
      {
        ...appointment,
        scheduledDateTime: moment.utc(appointment.scheduledDateTime).format("YYYY-MM-DDTHH:mm:ssZ"),
        ...(appointment.endDateTime && {
          endDateTime: moment.utc(appointment.endDateTime).format("YYYY-MM-DDTHH:mm:ssZ")
        }),
        staffMembers: appointment.staffMembers.map(({ id }) => id).filter(id => id),
        attendees: appointment.attendees.map(({ id }) => id)
      },
      {
        onSuccess: () => {
          navigate(`/inquiry/${inquiry.id}`);
        }
      }
    );
  };

  const handleUpdateAppointmentStatus = (appointment: InquiryAppointment, options?: UpdateAppointmentStatusOptions) => {
    if (options?.selectedSubService && inquiryDetailQuery.data) {
      createOrUpdateInquiryMutator.mutate(
        {
          inquiryId: inquiry.id,
          formData: withUpdatedSelectedSubService(
            inquiryDetailQuery.data,
            options.selectedSubService,
            activeConstants.timezone
          )
        },
        {
          onSuccess: () => {
            applyAppointmentStatusUpdate(appointment);
          }
        }
      );
      return;
    }

    applyAppointmentStatusUpdate(appointment);
  };

  const handleRescheduleAppointment = (appointment: InquiryAppointment) => {
    setActiveAppointment(appointment);
    showAppointmentFormModal();
  };

  const handleCreateNewAppointment = () => {
    setActiveAppointment(undefined);
    showAppointmentFormModal();
  };

  const handleClickUpcomingAppointment = (appointment: InquiryAppointment) => {
    setSelectedUpcomingAppointment(appointment);
    showAppointmentSummaryModal();
  };

  const { showAppointmentFormModal, hideAppointmentFormModal, renderAppointmentFormModal } = useAppointmentFormModal({
    inquiryId: inquiry.id,
    appointment: activeAppointment,
    centerId: activeCenterId
  });

  const { showStudentDetailFormModal, renderStudentDetailFormModal } = useStudentDetailFormModal();

  const { showAppointmentSummaryModal, renderAppointmentSummaryModal } = useAppointmentSummaryModal({
    student: dashboardQuery.data?.student,
    familyInfo: dashboardQuery.data?.familyInfo,
    contacts: dashboardQuery.data?.attendees,
    appointment: selectedUpcomingAppointment,
    selectedSubService: inquiryDetailQuery.data?.selectedSubService,
    onUpdateAppointmentStatus: handleUpdateAppointmentStatus,
    onRescheduleAppointment: handleRescheduleAppointment
  });

  const serviceId = inquiryDetailQuery.data?.legacyClientId || "-";

  const isAppointmentActionProcessing = inquirySummaryQuery.data?.isProcessingStatus ?? inquiry.isProcessingStatus;

  useEffect(() => {
    if (isAppointmentActionProcessing) {
      return;
    }

    // Allow Bootstrap's close transition to finish before removing any orphan
    // left by the processing-complete refresh.
    const timeoutId = window.setTimeout(cleanupOrphanedAppointmentModalArtifacts, 300);
    return () => window.clearTimeout(timeoutId);
  }, [isAppointmentActionProcessing]);

  useEffect(() => {
    return () => {
      hideAppointmentFormModal();
      window.setTimeout(cleanupOrphanedAppointmentModalArtifacts, 300);
    };
    // Run only when this Inquiry Summary unmounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <LoadingOverlay
        loading={
          dashboardQuery.isLoading ||
          createEventMutator.isLoading ||
          updateEventMutator.isLoading ||
          createOrUpdateInquiryMutator.isLoading
        }
      >
        <div className="card-body">
          <ServiceIdSummary inquiryId={inquiry.id} serviceId={serviceId} onSelectServiceId={onSelectServiceId} />

          <StudentDetailsTable
            inquiryDashboardInfo={dashboardQuery.data?.inquiry || []}
            onClickStudent={showStudentDetailFormModal}
          />
          {renderStudentDetailFormModal()}

          <div className="d-flex mt-4 mb-4 align-items-center justify-content-between">
            <h3 className="card-subheader mb-0">Primary/Emergency Contacts</h3>
          </div>

          <FamilyContactsTable contacts={dashboardQuery.data?.contacts} onSelectContact={handleSelectContact} />

          <div className="d-flex mt-4 mb-4 align-items-center justify-content-between">
            <h3 className="card-subheader mb-0">Upcoming Appointments</h3>
            {hasPermission && hasPermission(INQUIRY_EDIT) && (
              <LoadingButton
                className="btn btn-primary btn-sm ms-2"
                onClick={handleCreateNewAppointment}
                isLoading={isAppointmentActionProcessing}
                loadingMessage="Processing..."
              >
                Create Appointment
              </LoadingButton>
            )}
          </div>

          <UpcomingEventsTable
            upcomingEvents={dashboardQuery.data?.upcomingEvents}
            selectedSubService={inquiryDetailQuery.data?.selectedSubService}
            onUpdateAppointmentStatus={handleUpdateAppointmentStatus}
            onRescheduleAppointment={handleRescheduleAppointment}
            onClickUpcomingEvent={handleClickUpcomingAppointment}
          />

          {renderAppointmentSummaryModal()}

          {renderFamilyContactFormModal()}
        </div>
        <AppointmentHistoryAccordion className="mt-4" appointmentHistory={dashboardQuery.data?.pastEvents} />
      </LoadingOverlay>

      {/* Keep Create Appointment outside the Summary loading layer so it cannot be covered. */}
      {renderAppointmentFormModal()}
    </>
  );
};
